import { useState } from "react";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Heart, X } from "lucide-react";

export default function AiAssistButton({ context, receiverId, extra, onPick, label = "suggest for me" }) {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const isCupid = user?.gender === "female";
  const brand = isCupid ? "AI Cupid" : "AI Wingman";
  const BrandIcon = isCupid ? Heart : Sparkles;

  const fetchSuggestions = async () => {
    if (loading || !receiverId) return;
    setLoading(true);
    try {
      const response = await api.post("/ai/assist", { context, receiver_id: receiverId, extra: extra || {} });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't get suggestions right now");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      {suggestions.length > 0 && (
        <div data-testid={`ai-assist-suggestions-${context}`}>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <BrandIcon className={`w-3.5 h-3.5 ${isCupid ? "text-rose-400 fill-rose-400" : "text-primary"}`} /> {brand} — tap to use
            </p>
            <button
              type="button"
              onClick={() => setSuggestions([])}
              className="text-muted-foreground hover:text-white"
              data-testid={`ai-assist-dismiss-${context}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-col gap-1.5">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                className="text-left text-sm px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors"
                onClick={() => {
                  onPick(s);
                  setSuggestions([]);
                }}
                data-testid={`ai-assist-suggestion-${context}-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={isCupid ? "border-rose-400/40 text-rose-400 hover:bg-rose-500/10" : "border-primary/30 text-primary hover:bg-primary/10"}
        onClick={fetchSuggestions}
        disabled={loading}
        data-testid={`ai-assist-btn-${context}`}
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-b-2 border-current mr-2"></div>
            Thinking...
          </>
        ) : (
          <>
            <BrandIcon className={`w-3.5 h-3.5 mr-2 ${isCupid ? "fill-rose-400" : ""}`} />
            {brand}: {label}
          </>
        )}
      </Button>
    </div>
  );
}
